/**
 * Formatting helpers for Labor & staffing panel rows — kept pure so
 * grade-range / headcount / times-line gating can be unit-tested without
 * rendering React.
 */

import type { PlantHumanLaborRef } from '@axilon-apps/domains-ontology-ui-state'

/**
 * Simultaneous demand while one HumanLabor operation executes
 * (`REQUIRES_LABOR.num_employees`). Distinct from CrewRequirement capacity.
 */
export function formatOperationDemand(numEmployees: number | undefined): string | null {
  if (typeof numEmployees !== 'number' || !Number.isFinite(numEmployees)) return null
  const n = Math.trunc(numEmployees)
  return `${n} required while running`
}

/** Compact chip for flow tiles / badges: `× N`. */
export function formatHeadcount(numEmployees: number | undefined): string | null {
  if (typeof numEmployees !== 'number' || !Number.isFinite(numEmployees)) return null
  return `× ${Math.trunc(numEmployees)}`
}

/** Shared CrewRequirement capacity label. */
export function formatCrewCapacity(headcount: number | undefined): string | null {
  if (typeof headcount !== 'number' || !Number.isFinite(headcount)) return null
  const n = Math.trunc(headcount)
  return n === 1 ? '1 person shared capacity' : `${n} people shared capacity`
}

const OPERATION_KIND_LABELS: Record<string, string> = {
  load: 'load',
  unload: 'unload',
  transfer: 'transfer',
  manual_process: 'manual process',
  inspection: 'inspection',
  pack: 'pack',
  other: 'other'
}

export function formatOperationKind(kind: string | undefined): string | null {
  if (!kind) return null
  return OPERATION_KIND_LABELS[kind] ?? kind.replaceAll('_', ' ')
}

const USAGE_KIND_LABELS: Record<string, string> = {
  source: 'source',
  destination: 'destination',
  tool: 'tool',
  fixture: 'fixture',
  processor: 'processor',
  inspection: 'inspection',
  other: 'other'
}

/** Display label for `USES_EQUIPMENT.usage_kind`. */
export function formatUsageKind(kind: string | undefined): string | null {
  if (!kind) return null
  return USAGE_KIND_LABELS[kind] ?? kind.replaceAll('_', ' ')
}

/**
 * Role chip label: `Operator · gr 2–4`. Omits the grade clause when neither
 * bound is set; shows a single bound as `gr ≥2` / `gr ≤4`.
 */
export function formatRoleChipLabel(
  roleName: string,
  minGrade: number | undefined,
  maxGrade: number | undefined
): string {
  const grade = formatGradeRange(minGrade, maxGrade)
  return grade ? `${roleName} · ${grade}` : roleName
}

export function formatGradeRange(
  minGrade: number | undefined,
  maxGrade: number | undefined
): string | null {
  const hasMin = typeof minGrade === 'number' && Number.isFinite(minGrade)
  const hasMax = typeof maxGrade === 'number' && Number.isFinite(maxGrade)
  if (hasMin && hasMax) {
    if (minGrade === maxGrade) return `gr ${minGrade}`
    return `gr ${minGrade}–${maxGrade}`
  }
  if (hasMin) return `gr ≥${minGrade}`
  if (hasMax) return `gr ≤${maxGrade}`
  return null
}

/**
 * Cycle/manual/auto/walk + pieces-per-cycle line. Returns null when none
 * of the optional timing props are present (schema props land later).
 */
export function formatLaborTimesLine(task: PlantHumanLaborRef): string | null {
  const bits: string[] = []
  if (typeof task.cycle_time_s === 'number' && Number.isFinite(task.cycle_time_s)) {
    bits.push(`${task.cycle_time_s}s cycle`)
  }
  if (typeof task.manual_time_s === 'number' && Number.isFinite(task.manual_time_s)) {
    bits.push(`${task.manual_time_s}s manual`)
  }
  if (typeof task.auto_time_s === 'number' && Number.isFinite(task.auto_time_s)) {
    bits.push(`${task.auto_time_s}s auto`)
  }
  if (typeof task.walk_time_s === 'number' && Number.isFinite(task.walk_time_s)) {
    bits.push(`${task.walk_time_s}s walk`)
  }
  if (typeof task.pieces_per_cycle === 'number' && Number.isFinite(task.pieces_per_cycle)) {
    bits.push(`${task.pieces_per_cycle} pcs/cycle`)
  }
  return bits.length > 0 ? bits.join(' · ') : null
}
